import React, { useState } from 'react';
import { Input, LogoComponent, OnboardingContainer } from '../../../components';
import {
  AcceptAgreement,
  AgreementContainer,
  AgreementContent,
  AgreementText,
  BackIconWrapper,
  CheckboxContainer,
  CheckboxText,
  CheckboxWrapper,
  Content,
  Form,
  FormHeader,
  Header,
  StyledButton,
  Title,
} from './UpdatePasswordPage.styles';
import { notifyError } from '../../../utils/notify';
import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import { useForm } from 'react-hook-form';
import { createInfluencerRequestPassword } from '../../../store/slices/influencerRequest/asyncThunks';
import { useDispatch, useSelector } from 'react-redux';
import { selectInfluencerRequestActionsPending } from '../../../store/slices/influencerRequest/slice';
import { Text24Bold } from '../../../components/utils/Text/Text.styles';
import { ReactComponent as CheckIcon } from '../../../assets/icons/check.svg';
import { ReactComponent as CheckEmptyIcon } from '../../../assets/icons/check-empty.svg';
import { ReactComponent as BackIcon } from '../../../assets/icons/chevron_left.svg';
import { setUserStatus } from '../../../store/slices/user/slice';

export const updatePasswordFormSchema = yup
  .object({
    password: yup.string().trim().required('Password is required'),
  })
  .required();

const UpdatePasswordPage = () => {
  const { createInfluencerRequestPassword: createInfluencerRequestPasswordPending } = useSelector(
    selectInfluencerRequestActionsPending,
  );
  const dispatch = useDispatch();
  const [agreementStep, setAgreementStep] = useState(true);
  const [acceptedAgreement, setAcceptedAgreement] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm({
    defaultValues: {
      password: '',
    },
    delayError: 300,
    resolver: yupResolver(updatePasswordFormSchema),
  });

  const onSubmit = async data => {
    const { password } = data;
    dispatch(createInfluencerRequestPassword({ password }))
      .unwrap()
      .then(({ userStatus }) => {
        dispatch(setUserStatus(userStatus));
      })
      .catch(err => {
        notifyError(err.message);
      });
  };

  return (
    <OnboardingContainer hideHeader>
      <Content>
        {agreementStep ? (
          <AgreementContainer>
            <Header>
              <Text24Bold>Terms & Conditions</Text24Bold>
            </Header>
            <AgreementContent>
              <AgreementText>
                1. Acceptance of Terms
                <br />
                By creating a TLINK account, clicking “I Agree, ” or otherwise uploading content, you (“Creator, ” “you,
                ” or “your”) irrevocably accept and agree to be bound by these T&C as of that date (“Effective Date”).
              </AgreementText>
              <AgreementText>
                2. Definitions
                <br />
                “Account” – your user credentials on creators.tlink.me.
                <br /> “Content” – any photo, video, audio, text, stream, AI generation, metadata, or other material
                uploaded or transmitted via TLINK. <br />
                “Gross Revenue” – all amounts actually collected from end-users for access to your TLINK profile, paid
                chats, media sales, tipping, and other monetized interactions before any deduction other than (i)
                VAT/GST or similar sales taxes remitted to authorities and (ii) chargebacks/refunds. <br />
                “Net Revenue” – Gross Revenue minus (i) chargebacks/refunds actually processed after the 21-day holding
                period, (ii) the TLINK platform fee, and (iii) the Telegram platform fee. <br />
                “Referral Creator” – a creator onboarded through a unique referral link or code you generated.
              </AgreementText>
              <AgreementText>
                3. Eligibility & Verification
                <br />
                1. Age. You must be at least 18 years old and legally able to enter into contracts.
                <br /> 2. KYC/AML. You agree to complete identity and sanctions screening at onboarding and before each
                withdrawal request. We may engage third-party verification services and store related data as permitted
                by our [Privacy Policy].
                <br /> 3. Accuracy. All information you provide must be complete and kept current. Failure may result in
                suspension. <br />
                4. Account Security. You are solely responsible for maintaining the confidentiality of your login
                credentials and for all activities that occur under your Account. TLINK is
              </AgreementText>
              <AgreementText>
                4. Services & License Grants
                <br />
                TLINK is not Telegram. It is a monetization tool that operates on the Telegram platform, enabling
                creators to deliver and manage paid content via Telegram channels and chats.
                <br /> 1. Hosting & Paywall. TLINK hosts, paywalls, and delivers your Content to end-users on Telegram
                channels and chats you authorize. <br />
                2. Platform License to Creator. We grant you a limited, revocable, non-exclusive license to access the
                dashboard and related APIs solely for legitimate creator monetization. <br /> 3. Creator License to
                TLINK. <br />
                a. Hosting License. For as long as your Account remains active and for so long as we maintain archival
                backups thereafter, you grant TLINK a worldwide, non-exclusive, royalty-free license to host, store,
                reproduce, distribute, publicly display, and perform your Content for end-users according to your
                paywall settings. <br />
                b. Promotional License. While your Account is active, you grant TLINK a worldwide, royalty-free license
                to use your name, image, likeness, trademarks, and “digital persona” (including AI renderings) in TLINK
                marketing, without further approval. <br /> c. Reservation of Rights. You retain all ownership in the
                underlying Content; TLINK owns all rights in the Platform, its compilations, and any anonymized or
                aggregate data.
              </AgreementText>

              <AgreementText>
                5. Creator Earnings & Payment
                <br />
                1. Revenue Share. You are entitled to 50 % of Gross Revenue generated by your profile (the “Creator
                Share”).
                <br /> 2. Holding Period. All Gross Revenue is subject to a 21-day clearance period to manage fraud risk
                and refunds. <br /> 3. Withdrawals. After clearance, you may instruct TLINK to pay available balances
                via (i) bank wire transfer or (ii) crypto stable-coin (e.g., USDC on Ethereum). TLINK may impose minimum
                withdrawal amounts, network-gas fees, or banking charges, each disclosed in the dashboard at the time of
                withdrawal.
                <br /> 4. Chargebacks & Refunds. If an end-user obtains a chargeback or refund after funds were paid to
                you, TLINK will deduct the corresponding amount (plus card-scheme penalties, if any) from your next
                payout(s). You remain ultimately liable for negative balances.
                <br /> 5. Taxes. You are solely responsible for reporting and paying all income, payroll, VAT, or
                similar taxes arising from Creator Share. TLINK may withhold amounts where required by law.
                <br /> 6. Statements. Transaction history is available in the dashboard and is conclusive absent
                manifest error.
              </AgreementText>
              <AgreementText>
                6. Referral Program
                <br />
                1. Direct Referrals. For each Referral Creator, you earn 4 % of such creator’s Gross Revenue for as long
                as they remain active unless TLINK modifies the plan (see Section 13).
                <br /> 2. Cap & Plan Changes. TLINK may revise percentages, duration, or structure at any time without
                notic
              </AgreementText>
              <AgreementText>
                7. Content Standards & Prohibited Conduct
                <br />
                1. Illegal or Harmful Content. You must not upload, post, or facilitate any Content that:
                <br /> ○ Depicts or exploits minors (real, fictional, or simulated);
                <br /> ○ Involves bestiality, necrophilia, sexual violence, trafficking, non-consensual acts, incest, or
                other unlawful or taboo material;
                <br /> ○ Is deepfake or otherwise manipulated without documented consent of depicted persons;
                <br /> ○ Promotes hatred or violence against a protected class;
                <br /> ○ Contains private personal data, revenge porn, or defamation;
                <br /> ○ Infringes third-party IP or violates any law.
                <br /> 2. AI & Automation Disclosure Ban. You agree to maintain a consistent and authentic user
                experience in connection with TLINK’s Telegram-based services. You must not disclose, suggest, or imply
                to end-users that any interaction is AI-generated or automated unless such disclosure is expressly
                required by applicable law. <br />
                In such cases, you must notify TLINK in writing prior to making any required disclosure and cooperate
                fully to ensure such compliance is handled in a manner that minimizes disruption to the user experience
                and adheres to Company guidelines.
                <br /> Any unauthorized or misleading disclosure related to automation or AI constitutes a material
                breach. TLINK may seek injunctive relief and liquidated damages of €50,000 (fifty-thousand Euro) per
                violation, acknowledged by the parties as a reasonable pre-estimate of harm.
                <br />
                3. You remain solely responsible for compliance with all applicable laws, regulations, and platform
                policies.
                <br /> 4. Brand Guidelines & Link Titles. Your public links and channel names must comply with TLINK
                branding guides (e.g., “My Telegram”) and must not mislead users. Repeated violations may result in
                suspension.
                <br /> 5. Co-authored Content: <br />
                a. You are legally responsible for all Content you upload. You may upload Content that features someone
                other than you ("Co-Authored Content").
                <br /> b. You warrant that each individual in Co-Authored Content you upload is:
                <br />
                i. a Creator you have tagged using the tool available on TLINK; or <br />
                ii. an individual at least 18 years of age whose proof of identity and whose written, informed consent
                you have obtained to share the Content on TLINK.
                <br /> c. You and each Co-Author will provide us with necessary documents to confirm you have satisfied
                the requirements of these Terms of Service.
                <br /> d. If you, or anyone appearing in Co-Authored Content, do not provide any information we request,
                we may restrict or delete the Co-Authored Content or your account, and/or withhold all or any portion of
                your Creator Earnings.
                <br /> e. We are not a party to any agreement that you have to create Co-Authored Content or any
                resulting dispute. All claims arising from Co-Authored Content shall be made against the Creator(s) who
                posted or who appear in the Co-Authored Content. You will not make any claims against us, and you
                release us from any claims against us, arising from Co-Authored Content.
                <br /> f. Any individual(s) who appear in the Co-Authored Content may, at any time, withdraw their
                consent to appear in that Co-Authored Content. Co-Authored Content may be deleted where a participant
                withdraws consent.
                <br />
                6. Brand Misrepresentation. You must not impersonate TLINK, falsely suggest sponsorship, or create
                content, links, or channels that imply official TLINK status. <br />
                7. Community Compliance. You agree to comply with Telegram’s Terms of Service at all times.
              </AgreementText>
              <AgreementText>
                8. Intellectual-Property Claims & DMCA
                <br />
                TLINK responds to valid takedown notices and may remove Content or disable accounts upon receipt of a
                lawful IP-infringement claim. Repeat infringers will be terminated.
              </AgreementText>
              <AgreementText>
                9. Confidentiality
                <br />
                You shall keep all non-public information about TLINK’s technology, pricing, or operations strictly
                confidential, including but not limited to any AI or automation systems powering chats.
              </AgreementText>
              <AgreementText>
                10. Term, Termination & Suspension
                <br />
                1. Term. This Agreement commences on the Effective Date and continues until terminated.
                <br /> 2. Creator Termination. You may terminate at will by emailing support@tlink.me,
                partners@tlink.me, and partnerships@tlink.me. Termination is effective when TLINK confirms receipt or
                within five (5) business days, whichever is earlier.
                <br /> 3. TLINK Suspension/Termination. TLINK may, in its sole discretion and without notice, suspend
                payouts, remove Content, or terminate your Account for any reason, including suspected breach, fraud, or
                reputational risk. <br />
                4. Post-Termination Rights.
                <br /> ○ Earnings. Cleared balances will be paid on the next scheduled payout cycle. Balances subject to
                ongoing disputes or potential chargebacks will be held until resolved.
                <br /> ○ Content Retention. TLINK may retain server copies and archival backups for security,
                compliance, or evidence purposes unless you specifically request permanent deletion in writing. <br />○
                Survival. Sections 5, 6, 7, 8, 9, 11, 12, and 14–18 survive termination.
              </AgreementText>
              <AgreementText>
                11. Modifications to T&C
                <br />
                TLINK may modify these T&C (including revenue terms) at any time without prior notice. Updated terms
                take effect upon posting to creators.tlink.me, and your continued use constitutes acceptance. If you
                disagree, your sole remedy is to terminate your Account under Section 10.2.
              </AgreementText>
              <AgreementText>
                12. Warranties & Disclaimers
                <br />
                1. Creator Warranty. You represent and warrant that: (i) you own or have lawful rights to all Content;
                (ii) all persons depicted are adults who have consented in writing; and (iii) uploading and monetizing
                the Content through TLINK does not violate any law or third-party right.
                <br />
                2. Platform Disclaimer. THE PLATFORM IS PROVIDED “AS IS” WITHOUT WARRANTIES OF ANY KIND, EXPRESS OR
                IMPLIED. TLINK DOES NOT WARRANT UNINTERRUPTED SERVICE, ERROR-FREE OPERATION, OR PROFITABILITY.
              </AgreementText>
              <AgreementText>
                13. Limitation of Liability
                <br />
                To the maximum extent permitted by law, TLINK, its affiliates, officers, and employees shall not be
                liable for indirect, incidental, special, consequential, or punitive damages, or for lost profits, even
                if advised of the possibility. TLINK’s aggregate liability under these T&C shall not exceed the total
                Creator Share actually paid to you during the six-month period immediately preceding the claim.
              </AgreementText>
              <AgreementText>
                14. Indemnification
                <br />
                You will defend, indemnify, and hold harmless TLINK and its affiliates against any third-party claim
                arising out of (i) your Content, (ii) your breach of these T&C, or (iii) your violation of law.
              </AgreementText>
              <AgreementText>
                15. Governing Law & Dispute Resolution
                <br />
                1. Governing Law. These T&C are governed by the laws of Cyprus, excluding conflict-of-law rules.
                <br />
                2. Arbitration. Any dispute shall be finally settled by binding arbitration under the Cyprus Arbitration
                & Mediation Centre Rules. Seat: Nicosia. Language: English. Judgment on the award may be entered in any
                court of competent jurisdiction.
                <br /> 3. Class-Action Waiver. You and TLINK waive any right to litigate or arbitrate claims on a class,
                collective, or representative basis.
                <br /> 4. Injunctive Relief. Nothing herein prevents TLINK from seeking equitable relief for IP or
                confidentiality breaches in any competent court.
              </AgreementText>
              <AgreementText>
                16. Relationship of Parties
                <br />
                The parties are independent contractors. Nothing creates a partnership, joint venture, franchise, or
                employment relationship.
              </AgreementText>
              <AgreementText>
                17. Assignment
                <br />
                You may not assign or transfer your rights without TLINK’s prior written consent. TLINK may freely
                assign these T&C.
              </AgreementText>
              <AgreementText>
                18. Notices
                <br />
                Notices to TLINK must be sent to legal@tlink.me with a paper copy to the registered address of OCAI LTD.
                TLINK may provide notices to you via email or dashboard alert.
              </AgreementText>
              <AgreementText>
                19. Entire Agreement; Severability; Waiver
                <br />
                These T&C (including any policies linked or incorporated by reference) constitute the entire agreement
                and supersede all prior understandings relating to the subject matter. If any provision is held invalid,
                the remainder remains enforceable. TLINK’s failure to enforce any provision is not a waiver.
              </AgreementText>
            </AgreementContent>
            <AcceptAgreement>
              <CheckboxContainer>
                <CheckboxWrapper
                  onClick={() => {
                    setAcceptedAgreement(!acceptedAgreement);
                  }}>
                  {acceptedAgreement ? <CheckIcon color="black" /> : <CheckEmptyIcon color="black" />}
                </CheckboxWrapper>
                <CheckboxText
                  onClick={() => {
                    setAcceptedAgreement(!acceptedAgreement);
                  }}>
                  I accept Terms and Conditions.
                </CheckboxText>
              </CheckboxContainer>
              <StyledButton
                title="Continue"
                onClick={() => {
                  setAgreementStep(false);
                }}
                disabled={!acceptedAgreement}
              />
            </AcceptAgreement>
          </AgreementContainer>
        ) : (
          <>
            <Form onSubmit={handleSubmit(onSubmit)}>
              <FormHeader>
                <BackIconWrapper onClick={() => setAgreementStep(true)}>
                  <BackIcon />
                </BackIconWrapper>
                <LogoComponent />
              </FormHeader>
              <Title>Create a password</Title>
              <Input
                placeholder="Enter password"
                {...register('password')}
                error={errors.password?.message}
                type="password"
              />
              <StyledButton title="Continue" type="submit" isLoading={createInfluencerRequestPasswordPending} />
            </Form>
          </>
        )}
      </Content>
    </OnboardingContainer>
  );
};

export default UpdatePasswordPage;
